import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { courses } from '../../data/courses';
import type { CourseCategory } from '../../types/content';
import { CourseCard } from './CourseCard';
export type CourseFilter = 'All' | CourseCategory;
export const courseFilters: CourseFilter[] = ['All', 'CCW', 'Handgun', 'Home Defense', 'Carbine', 'Women', 'NRA', 'USCCA', 'Private'];
interface CourseFilterGridProps {
  filter: CourseFilter;
  onFilterChange: (f: CourseFilter) => void;
  limit?: number;
}
export function CourseFilterGrid({
  filter,
  onFilterChange,
  limit
}: CourseFilterGridProps) {
  const filtered = useMemo(() => filter === 'All' ? courses : courses.filter(c => c.categories.includes(filter)), [filter]);
  const visible = limit ? filtered.slice(0, limit) : filtered;
  return <div data-id="element-83" data-mp="2mosatlzc7m7r">
      <div className="no-scrollbar -mx-[clamp(1rem,4vw,7rem)] flex gap-2 overflow-x-auto px-[clamp(1rem,4vw,7rem)] pb-1" role="group" aria-label="Filter courses" data-id="element-84" data-mp="2mp1xp0p35d98">
        {courseFilters.map(f => {
        const active = f === filter;
        return <button key={f} type="button" aria-pressed={active} onClick={() => onFilterChange(f)} className={`h-10 shrink-0 whitespace-nowrap rounded-full border px-4 text-sm font-semibold transition-colors duration-150 ${active ? 'border-navy bg-navy text-white' : 'border-navy/20 bg-white text-navy hover:border-navy/50'}`} data-id="element-85" data-mp="jfelbn3dztn0">
              {f}
            </button>;
      })}
      </div>
      <p className="mt-4 text-sm text-steel" aria-live="polite" data-id="element-86" data-mp="16ekjk5jh8od2">
        Showing {visible.length} of {filtered.length} {filtered.length === 1 ? 'course' : 'courses'}
      </p>
      <motion.ul layout className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 4xl:grid-cols-6" data-id="element-87" data-mp="1yvetbscx0uzt">
        <AnimatePresence mode="popLayout" data-id="element-88" data-mp="28qc65ur4tmlc">
          {visible.map(course => <motion.li key={course.slug} layout initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-89" data-mp="24r3fiyre24u0">
              <CourseCard course={course} data-id="element-90" data-mp="1ptksq1y9u1p7" />
            </motion.li>)}
        </AnimatePresence>
      </motion.ul>
      {limit && filtered.length > limit && <div className="mt-10 flex justify-center" data-id="element-91" data-mp="1gzy2ssgqvsnx">
          <Link to={filter === 'All' ? '/courses' : `/courses?filter=${encodeURIComponent(filter)}`} className="group inline-flex items-center gap-2 font-display text-lg font-semibold uppercase tracking-wider text-navy" data-id="element-92" data-mp="1h0ponbkjic10">
            View all {filtered.length} courses
            <ArrowRightIcon className="h-5 w-5 text-accent transition-transform duration-150 group-hover:translate-x-1" aria-hidden="true" data-id="element-93" data-mp="1248e2mk6d1fi" />
          </Link>
        </div>}
    </div>;
}